<template>
  <div class="input-section-wrapper pricing-section">
    <h3 class="input-section-title">
      Pricing
    </h3>
    <b-row
      class="w-100"
      no-gutters
    >
      <b-col
        md="4"
        class="reduce-padding"
      >
        <b-form-group
          class="input-form-group"
        >
          <template #label>
            <span class="input-label">Currency</span>
          </template>
          <b-form-input
            v-model="currency"
            placeholder="Enter Currency"
            disabled
          />
        </b-form-group>
      </b-col>
      <b-col
        md="4"
        class="reduce-padding"
      >
        <b-form-group
          class="currency-input-form-group"
        >
          <template #label>
            <span class="input-label">Wholesale Price *</span>
          </template>
          <ValidationProvider
            v-slot="{ errors, touched }"
            name="Wholesale Price"
            :rules="isSameWholesalePrice ? 'required|price' : ''"
          >
            <div
              class="currency-input-wrapper"
              :class="{
                'is-invalid': touched && errors.length,
                'disabled': !isSameWholesalePrice,
                'field-updated': isSameWholesalePrice && isFieldUpdated(wholesalePrice, originalWholesalePrice)
              }"
            >
              <span>{{ currency }}</span>
              <b-form-input
                v-model="wholesalePrice"
                :type="'text'"
                name="Wholesale Price"
                placeholder="Enter Wholesale Price"
                :disabled="!isSameWholesalePrice"
                :class="{ 'is-invalid': touched && errors.length }"
                @keypress="onlyAllowPositiveDecimal"
                @paste="blockInvalidPaste"
                @input="(newVal) => handlePriceChange('price', newVal)"
              />
            </div>
            <div class="error-section">
              <small class="text-danger">{{ errors[0] }}</small>
            </div>
          </ValidationProvider>
        </b-form-group>
      </b-col>
      <b-col
        md="4"
        class="reduce-padding"
      >
        <b-form-group
          class="currency-input-form-group"
        >
          <template #label>
            <span class="input-label">Retail Price *</span>
          </template>
          <ValidationProvider
            v-slot="{ errors, touched }"
            name="Retail Price"
            :rules="isSameRetailPrice ? 'required|price' : ''"
          >
            <div
              class="currency-input-wrapper"
              :class="{
                'is-invalid': touched && errors.length,
                'disabled': !isSameRetailPrice,
                'field-updated': isSameRetailPrice && isFieldUpdated(retailPrice, originalRetailPrice)
              }"
            >
              <span>{{ currency }}</span>
              <b-form-input
                v-model="retailPrice"
                :type="'text'"
                name="Retail Price"
                placeholder="Enter Retail Price"
                :disabled="!isSameRetailPrice"
                :class="{ 'is-invalid': touched && errors.length }"
                @keypress="onlyAllowPositiveDecimal"
                @paste="blockInvalidPaste"
                @input="(newVal) => handlePriceChange('retailPrice', newVal)"
              />
            </div>
            <div class="error-section">
              <small class="text-danger">{{ errors[0] }}</small>
            </div>
          </ValidationProvider>
        </b-form-group>
      </b-col>
    </b-row>

    <price-update-warning
      :show="showPriceUpdateWarning"
      :product-id="productId"
      :items="items"
      :wholesale-price="wholesalePrice"
      :retail-price="retailPrice"
      :original-wholesale-price="originalWholesalePrice"
      :original-retail-price="originalRetailPrice"
      @cancel="showPriceUpdateWarning = false"
      @close="showPriceUpdateWarning = false"
      @update-orders="handleOrderUpdates"
    />

  </div>
</template>
<script>
import {
 BCol, BFormGroup, BFormInput, BRow,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import store from '@/store'
import { UPDATE_PRODUCT_INFO } from '@/store/modules/productEdit.module'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import { blockInvalidPaste, onlyAllowPositiveDecimal } from '@/views/apps/product/edit/utils'
import PriceUpdateWarning from '../components/PriceUpdateWarning.vue'

export default {
  name: 'Pricing',
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    ValidationProvider,
    PriceUpdateWarning,
  },
  data() {
    return {
      wholesalePrice: null,
      retailPrice: null,
      isSameWholesalePrice: false,
      isSameRetailPrice: false,
      showPriceUpdateWarning: false,
      updateOrderPrices: true,
      priceChanged: false,
      ordersData: null,
      selectedOrders: [],
      selectAllOrders: false,
      orderSearchText: '',
      filteredOrders: [],
      radioOptions: [
        { text: 'No', value: true },
        { text: 'Yes, update prices', value: false },
      ],
      originalWholesalePrice: null,
      originalRetailPrice: null,
      updatedPriceTypes: [],
    }
  },
  computed: {
    currency() {
      return store.state.productEdit.savedState.currency
    },
    items: {
      get() {
        return store.state.productEdit.savedState.items
      },
      set(value) {
        store.commit(UPDATE_PRODUCT_INFO, { fieldName: 'items', newValue: value })
      },
    },
    productId() {
      return store.state.productEdit.savedState._id
    },
  },
  watch: {
    'ordersData.data.orders': {
      immediate: true,
      handler(orders) {
        if (orders) {
          this.filteredOrders = orders
        }
      },
    },
    showPriceUpdateWarning: {
      immediate: true,
      handler(value) {
        if (value) {
          analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_VIEWS_ORDER_UPDATE_POPUP, {
            productId: store.state.productEdit.savedState._id,
            collectionId: store.state.productEdit.savedState.collectionId,
            pricesChanged: this.updatedPriceTypes,
            newPrices: {
              wholesalePrice: this.wholesalePrice,
              retailPrice: this.retailPrice,
            },
            oldPrices: {
              wholesalePrice: this.originalWholesalePrice,
              retailPrice: this.originalRetailPrice,
            },
          })
        }
      },
    },
  },
  mounted() {
    const wholesalePrices = store.state.productEdit.savedState.items?.map(item => item.price).sort((a, b) => a - b)
    const retailPrices = store.state.productEdit.savedState.items?.map(item => item.retailPrice).sort((a, b) => a - b)

    if (wholesalePrices.length > 0 && wholesalePrices.every(price => price === wholesalePrices[0])) {
      this.isSameWholesalePrice = true
      this.wholesalePrice = wholesalePrices[0]
      this.originalWholesalePrice = wholesalePrices[0]
    } else {
      this.wholesalePrice = `${wholesalePrices[0]} - ${wholesalePrices[wholesalePrices.length - 1]}`
      this.originalWholesalePrice = wholesalePrices[0]
    }
    if (retailPrices.length > 0 && retailPrices.every(price => price === retailPrices[0])) {
      this.isSameRetailPrice = true
      this.retailPrice = retailPrices[0]
      this.originalRetailPrice = retailPrices[0]
    } else {
      this.retailPrice = `${retailPrices[0]} - ${retailPrices[retailPrices.length - 1]}`
      this.originalRetailPrice = retailPrices[0]
    }
  },
  methods: {
    blockInvalidPaste,
    onlyAllowPositiveDecimal,
    isFieldUpdated(currentValue, originalValue) {
      return originalValue !== null && Number(currentValue) !== originalValue
    },
    handlePriceChange(type, newVal) {
      this[type] = newVal

      this.items = this.items.map(item => ({
        ...item,
        price: Number(this.wholesalePrice),
        retailPrice: Number(this.retailPrice),
      }))

      const priceType = type === 'price' ? 'Wholesale Price' : 'Retail Price'

      this.updatedPriceTypes = [...new Set([...this.updatedPriceTypes, priceType])]

      // Check if either wholesale or retail price has changed
      const wholesalePriceChanged = this.originalWholesalePrice !== null && this.originalWholesalePrice !== Number(this.wholesalePrice)
      const retailPriceChanged = this.originalRetailPrice !== null && this.originalRetailPrice !== Number(this.retailPrice)
      this.priceChanged = wholesalePriceChanged || retailPriceChanged
    },
    handleOrderUpdates(updatedCount) {
      // Prepare an array of updated price types
      const updated = []
      if (this.originalWholesalePrice !== Number(this.wholesalePrice)) updated.push('Wholesale')
      if (this.originalRetailPrice !== Number(this.retailPrice)) updated.push('Retail')

      // Emit the event with count and prices updated
      this.$root.$emit('orders-price-updated', {
        count: updatedCount.length,
        prices: updated,
      })

      // Hide the warning modal
      this.showPriceUpdateWarning = false
    },
  },
}
</script>
<style lang="scss">
.pricing-section {
  margin-top: 24px;
}
.currency-input-form-group {
  margin-bottom: 0;

  .currency-input-wrapper {
    display: flex;
    align-items: center;
    gap: 0;
    padding: 0 16px;
    border: 1px solid #D8D6DD;
    border-radius: 5px;

    &.field-updated {
      background-color: #CEF0DB !important;
      border-color: #B3D1BF !important;

      .form-control {
        background-color: #CEF0DB !important;
      }
    }

    span {
      font-size: 12px;
      font-weight: 700;
    }
  }

  .disabled {
    background-color: #F5F5F5;
  }

  .is-invalid {
    border-color: #ea5455;
  }

  .form-control {
    font-size: 12px;
    border: none;
    padding-left: 8px !important;
    height: 36px;

    label {
      padding-top: 7px !important;
      font-size: 12px;
    }

    &:focus {
      box-shadow: none;
    }
  }

  input::placeholder {
    color: #B9B9B8;
  }

  input:placeholder-shown {
    text-overflow: ellipsis;
  }

  legend {
    padding-bottom: 4px !important;
  }
}
fieldset.currency-input-form-group legend {
  height: 20px;
  padding-bottom: 0 !important;
  margin-bottom: 4px !important;
  line-height: normal;
}
</style>
